import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { BUILD_GIT_SHA, BUILD_ID } from './buildInfo';
import { shouldDowngradeToWarning } from '../utils/errorCategory';
import { parseReportLocation, ReportLocation } from '../utils/stackTrace';

export type LogSeverity = 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL';

interface ReportedHttpRequest {
  method: string;
  url?: string;
  userAgent?: string;
}

interface ReportedUser {
  uid?: string;
  email?: string | null;
}

interface ReportedServiceContext {
  service: string;
  version: string;
}

export interface LogEventRequest {
  severity: LogSeverity;
  message: string;
  context?: Record<string, unknown>;
  stack?: string;
  reportLocation?: ReportLocation;
  httpRequest?: ReportedHttpRequest;
  user?: ReportedUser;
  serviceContext?: ReportedServiceContext;
}

const logFn = httpsCallable<LogEventRequest, { ok: true }>(functions, 'logFrontendEvent');

const SERVICE_NAME = 'aligned-crm-frontend';
const SERVICE_VERSION = BUILD_GIT_SHA || BUILD_ID || 'unknown';

function currentHttpRequest(): ReportedHttpRequest | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return undefined;
  }
  return {
    method: 'GET',
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
}

function currentServiceContext(): ReportedServiceContext {
  return { service: SERVICE_NAME, version: SERVICE_VERSION };
}

export async function logEventAsync(
  severity: LogSeverity,
  message: string,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    await logFn({ severity, message, context });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('logEvent failed:', err);
  }
}

export function logEvent(
  severity: LogSeverity,
  message: string,
  context?: Record<string, unknown>,
): void {
  void logEventAsync(severity, message, context);
}

export interface LogErrorOptions {
  context?: string;
  metadata?: Record<string, unknown>;
  user?: ReportedUser;
  severity?: LogSeverity;
}

function buildErrorMessage(error: unknown, contextLabel?: string): string {
  const base =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return String(error);
            } catch {
              return 'Unknown error';
            }
          })();
  return contextLabel ? `[${contextLabel}] ${base}` : base;
}

function extractStack(error: unknown): string | undefined {
  if (error instanceof Error && typeof error.stack === 'string') return error.stack;
  if (error && typeof (error as { stack?: unknown }).stack === 'string') {
    return (error as { stack: string }).stack;
  }
  return undefined;
}

export async function logErrorAsync(
  error: unknown,
  options: LogErrorOptions = {},
): Promise<void> {
  const stack = extractStack(error);
  const reportLocation = parseReportLocation(stack);
  const message = buildErrorMessage(error, options.context);
  const severity =
    options.severity || (shouldDowngradeToWarning(error) ? 'WARNING' : 'ERROR');

  try {
    await logFn({
      severity,
      message,
      context: options.metadata,
      stack,
      reportLocation,
      httpRequest: currentHttpRequest(),
      user: options.user,
      serviceContext: currentServiceContext(),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('logError failed:', err);
  }
}

export function logError(error: unknown, options: LogErrorOptions = {}): void {
  void logErrorAsync(error, options);
}
